import { createElement, useState, type KeyboardEvent } from 'react';
import Popover from '@mui/material/Popover';
import { overlayPaneProps } from './overlayPane';

export interface SelectPanelOption {
  key: string;
  label: string;
  selected: boolean;
}

interface SelectPanelProps {
  /** The form field the panel hangs from; the panel is as wide as it (CDK overlay of `mat-select`). */
  anchorEl: HTMLElement | null;
  open: boolean;
  /** Backdrop click, Escape, or focus leaving through the Popover (`reason` as MUI reports it). */
  onClose: (reason?: 'backdropClick' | 'escapeKeyDown') => void;
  options: readonly SelectPanelOption[];
  /** `source` tells a click on the option from Enter/Space in the panel. */
  onSelect: (option: SelectPanelOption, source: 'mouse' | 'keyboard') => void;
  /** Extra classes on the panel (Angular `panelClass`). */
  className?: string;
  labelledBy?: string;
  ariaLabel?: string;
  /** Sets the panel width to the field width (default) or only its minimum. */
  exactWidth?: boolean;
  /** Leave focus where it lands when the panel closes (a bare `mat-select` restores it by hand, like Material). */
  disableRestoreFocus?: boolean;
  /**
   * Keep DOM focus on the element that opened the panel, like Material (options are never focused by the panel; the
   * host's key handler drives the active option). The panel then does not handle keys or move focus itself.
   */
  keepFocus?: boolean;
  /** Controlled active option (highlighted); defaults to internal state driven by hover and the arrow keys. */
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
}

/**
 * The overlay of `<mat-select>`: `div.mat-mdc-select-panel[role=listbox]` holding `<mat-option>` elements with the
 * same classes and structure Material renders, below the field (MUI Popover supplies positioning, outside click and
 * Escape). Arrow keys move the active option, Enter selects it.
 */
export function SelectPanel({
  anchorEl,
  open,
  onClose,
  options,
  onSelect,
  className = '',
  labelledBy,
  ariaLabel,
  exactWidth = true,
  disableRestoreFocus = false,
  keepFocus = false,
  activeIndex,
  onActiveIndexChange,
}: SelectPanelProps) {
  const selectedIndex = options.findIndex((option) => option.selected);
  const [innerActive, setInnerActive] = useState<number | null>(null);
  const active = activeIndex ?? innerActive;
  const setActive = (index: number) => {
    setInnerActive(index);
    onActiveIndexChange?.(index);
  };
  const current = active ?? Math.max(selectedIndex, 0);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(Math.min(current + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(Math.max(current - 1, 0));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (options[current]) onSelect(options[current], 'keyboard');
    }
  };

  const width = anchorEl?.offsetWidth;
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={(_event, reason) =>
        onClose(reason === 'backdropClick' || reason === 'escapeKeyDown' ? reason : undefined)
      }
      disableRestoreFocus={disableRestoreFocus}
      disableAutoFocus={keepFocus}
      disableEnforceFocus={keepFocus}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        root: {
          slotProps: {
            backdrop: {
              invisible: true,
              className: 'cdk-overlay-backdrop cdk-overlay-transparent-backdrop cdk-overlay-backdrop-showing',
            },
          },
        },
        paper: overlayPaneProps(exactWidth ? { width } : { minWidth: width }),
      }}
    >
      <div
        role="listbox"
        tabIndex={-1}
        className={`mat-mdc-select-panel mdc-menu-surface mdc-menu-surface--open ${className}`.trim()}
        aria-labelledby={labelledBy}
        aria-label={ariaLabel}
        aria-multiselectable="false"
        onKeyDown={keepFocus ? undefined : onKeyDown}
      >
        {options.map((option, index) =>
          createElement(
            'mat-option',
            {
              key: option.key,
              role: 'option',
              className: `mat-mdc-option mdc-list-item${option.selected ? ' mdc-list-item--selected' : ''}${index === current ? ' mat-mdc-option-active' : ''}`,
              'aria-selected': String(option.selected),
              'aria-disabled': 'false',
              // Material: `tabindex="0"`, so a click focuses the option (and takes focus off the select's host)
              tabIndex: 0,
              onClick: () => onSelect(option, 'mouse'),
              onMouseEnter: () => setActive(index),
            },
            <span className="mdc-list-item__primary-text">{option.label}</span>,
            <div aria-hidden="true" className="mat-ripple mat-mdc-option-ripple mat-mdc-focus-indicator"></div>,
          ),
        )}
      </div>
    </Popover>
  );
}
